"use client";

//singlas106+test1@gmail.com single user subscription
//singlas106+test2@gmail.com team-user inactive
//singlas106+4@gmail.com -- single user plan
//inactiva user --singlas106+test2@gmail.com --- testing result: active and inactive is ok
//https://a923df388772.ngrok-free.app/stripe/webhook
//

import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../loading/Loading";
import { initializeApp } from "@/state/store";
import DashboardSidebar from "../dasboard-sidebar/DashboardSidebar";
import { Menu } from "lucide-react";
import { setIsSidebarOpen, logout, setToken } from "@/state";
import { toast } from "sonner";
import { useCompleteCheckoutSessionMutation } from "@/state/api";

const ClientRedirectWrapper = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const data = useSelector((state: any) => state.global.info);
  const user = useSelector((state: any) => state.global.user);
  const [ready, setReady] = useState(false);
  const [completeCheckoutSession] = useCompleteCheckoutSessionMutation();
  const isSidebarOpen = useSelector(
    (state: any) => state.global?.sideBar?.isSidebarOpen ?? false
  );
  const checkoutSessionId = searchParams.get("checkout_session_id");
  const subscription = user.subscription;
  let isActiveTeamSubscription = false;
  let isActiveSubscription = false;
  let isActiveTeamUserSubscription = false;
  let isDeletedTeamUserSubscription = false;
  if (subscription) {
    const now = new Date();

    const startDate = new Date(subscription.startDate);
    const endDate = new Date(subscription.endDate);

    if (
      subscription?.isOwner === true &&
      subscription.planType === "TEAM" &&
      startDate <= now &&
      endDate >= now
    ) {
      isActiveTeamSubscription = true;
      isActiveTeamUserSubscription = false;
      isDeletedTeamUserSubscription = false;
      isActiveSubscription = false;
    } else if (
      subscription.planType === "TEAMUSER" &&
      (subscription.status === "ACTIVE" ||
        subscription.status === "INACTIVE") &&
      startDate <= now &&
      endDate >= now
    ) {
      isActiveTeamUserSubscription = true;
      isDeletedTeamUserSubscription = false;
      isActiveSubscription = false;
      isActiveTeamSubscription = false;
    } else if (
      subscription.planType === "TEAMUSER" &&
      subscription.status === "DELETED" &&
      startDate <= now &&
      endDate >= now
    ) {
      isDeletedTeamUserSubscription = true;
      isActiveSubscription = false;
      isActiveTeamSubscription = false;
      isActiveTeamUserSubscription = false;
    } else if (
      startDate <= now &&
      endDate >= now &&
      subscription?.planType === "USER"
    ) {
      isActiveSubscription = true;
      isActiveTeamSubscription = false;
      isActiveTeamUserSubscription = false;
      isDeletedTeamUserSubscription = false;
    }
  } else {
    isActiveTeamSubscription = false;
    isActiveSubscription = false;
    isActiveTeamUserSubscription = false;
    isDeletedTeamUserSubscription = false;
  }
  const hasAnyAccess =
    isActiveTeamSubscription ||
    isActiveSubscription ||
    isActiveTeamUserSubscription;

  // Where a user belongs when they try to open something they cannot access.
  // No access at all -> /plans, which is the price grid when payments are on and
  // the "Get free trial" screen when they are off. A removed team member is the
  // exception: /plans bounces them back to "/", so send them there directly
  // rather than through a redirect that would ping-pong.
  const noAccessRedirect =
    !hasAnyAccess && !isDeletedTeamUserSubscription ? "/plans" : "/";

  // Sends the user back to sign-in and, crucially, clears the redux store as well
  // as localStorage. Clearing only localStorage leaves a user object in memory,
  // which AuthRedirectWrapper reads and bounces straight back to "/" - the two
  // wrappers then ping-pong forever behind a loading spinner.
  const forceSignOut = () => {
    localStorage.clear();
    dispatch(logout());
    dispatch(setToken({ access: "" }));
    router.push("/sign-in");
  };

  const checkAndRedirect = async () => {
    const result = await initializeApp();
    if (result?.isError) {
      // The server was unreachable (a restart, for example) rather than rejecting
      // the session. Keep the user signed in and render from persisted state
      // instead of throwing them out of a session that is still valid.
      if (result?.isTransportError) {
        toast.error("Can't reach the server. Showing your last loaded data.", {
          style: { background: "#fff", color: "red" },
          duration: 5000,
          closeButton: true,
          position: "top-right",
        });
        setReady(true);
        return;
      }
      forceSignOut();
    } else {
      if (checkoutSessionId) {
        try {
          await completeCheckoutSession({
            sessionId: checkoutSessionId,
          }).unwrap();
        } catch (error) {
          console.error("Failed to complete checkout session:", error);
        }

        const refreshedUser = await initializeApp();
        if (refreshedUser?.isError && !refreshedUser?.isTransportError) {
          forceSignOut();
          return;
        }

        const nextParams = new URLSearchParams(searchParams.toString());
        nextParams.delete("checkout_session_id");
        const nextPath = nextParams.toString()
          ? `${pathName}?${nextParams.toString()}`
          : pathName;

        router.replace(nextPath);
      }

      setReady(true);
    }
  };

  useEffect(() => {
    checkAndRedirect();
  }, [checkoutSessionId]);

  const allowed_routes = [
    "/contact-us",
    "/all-pitches",
    "/plans",
    "/plans/select-seat",
    "/payment",
    "/my-profile",
    "/",
    "/notifications",
  ];
  const getRedirectPath = () => {
    if (user.id === "" || user.email === "") {
      return "/sign-in";
    } else {
      if (allowed_routes.includes(pathName)) {
        return pathName;
      }

      if (
        pathName === "/team-dashboard" ||
        pathName === "/team-settings" ||
        pathName === "/select-seats" ||
        pathName === "/seats-payment" ||
        pathName.includes("/user-profile") ||
        pathName.includes("/user-pitch")
      ) {
        if (isActiveTeamSubscription) {
          return pathName;
        } else {
          return noAccessRedirect;
        }
      }

      if (pathName.includes("/feedback")) {
        if (hasAnyAccess) {
          return pathName;
        } else {
          return noAccessRedirect;
        }
      }

      if (pathName.includes("/plans")) {
        if (
          !isActiveTeamSubscription &&
          !isActiveSubscription &&
          !isActiveTeamUserSubscription &&
          !isDeletedTeamUserSubscription
        ) {
          return pathName;
        } else {
          return "/";
        }
      }

      if (pathName === "/about-you") {
        if (user.role === "" || user.role === "empty") {
          return "/role-selection";
        } else if (user.role === "teamLeader" || user.role === "professional") {
          return "/about-company";
        }
      } else if (pathName === "/about-company") {
        if (user.role === "" || user.role === "empty") {
          return "/role-selection";
        } else if (user.role === "user") {
          return "/about-you";
        }
      }

      if (pathName === "/seller-industry") {
        if (
          (isActiveTeamSubscription ||
            isActiveSubscription ||
            isActiveTeamUserSubscription) &&
          !isDeletedTeamUserSubscription
        ) {
          if (user.role === "" || user.role === "empty") {
            return "/role-selection";
          }
          if (
            user.role === "user" &&
            (user.aboutYou.name === "" || user.aboutYou.role === "")
          ) {
            return "/about-you";
          } else if (
            (user.role === "teamLeader" || user.role === "professional") &&
            (user.aboutCompany.name === "" ||
              user.aboutCompany.companyName === "" ||
              user.aboutCompany.jobTitle === "")
          ) {
            return "/about-company";
          }
        } else {
          return noAccessRedirect;
        }
      }

      if (
        !data?.industry &&
        ![
          "/",
          "/about-you",
          "/about-company",
          "/role-selection",
          "/seller-industry",
          "/contact-us",
          "/all-pitches",
          "/plans/select-seat",
          "/payment",
          "/plans",
        ].includes(pathName)
      ) {
        if (hasAnyAccess) {
          return "/seller-industry";
        } else {
          return noAccessRedirect;
        }
      }

      if (
        !data?.product &&
        ![
          "/",
          "/seller-industry",
          "/seller/selling-info",
          "/about-you",
          "/about-company",
          "/role-selection",
          "/contact-us",
          "/all-pitches",
          "/plans/select-seat",
          "/payment",
          "/plans",
        ].includes(pathName)
      ) {
        if (hasAnyAccess) {
          return "/seller/selling-info";
        } else {
          return noAccessRedirect;
        }
      }

      if (
        !data?.targetBuyer &&
        ![
          "/",
          "/seller-industry",
          "/seller/selling-info",
          "/business-model",
          "/role-selection",
          "/about-you",
          "/about-company",
          "/contact-us",
          "/all-pitches",
          "/plans/select-seat",
          "/payment",
          "/plans",
        ].includes(pathName)
      ) {
        if (hasAnyAccess) {
          return "/business-model";
        } else {
          return noAccessRedirect;
        }
      }

      if (
        isActiveTeamSubscription ||
        isActiveSubscription ||
        isActiveTeamUserSubscription
      ) {
        if (pathName === "/customer/selling-to") {
          if (!data?.targetBuyer || data.targetBuyer === "b2c")
            return "/business-model";
        }
        if (pathName === "/b2c/ideal-customer") {
          if (!data?.targetBuyer || data.targetBuyer === "b2b")
            return "/business-model";
        }

        if (pathName === "/customer/industry") {
          if (!data?.targetBuyer || data.targetBuyer === "b2c")
            return "/business-model";
          if (!data?.b2b?.persona) return "/customer/selling-to";
        }

        if (pathName === "/b2c/buyer-persona") {
          if (!data?.targetBuyer || data.targetBuyer === "b2b")
            return "/business-model";
          if (!data?.b2c?.customer) return "/b2c/ideal-customer";
        }

        if (pathName === "/b2c/motivates") {
          if (!data?.targetBuyer || data.targetBuyer === "b2b")
            return "/business-model";
          if (!data?.b2c?.age || !data?.b2c?.gender || !data?.b2c?.income)
            return "/b2c/buyer-persona";
        }

        if (pathName === "/other-info" || pathName === "/difficulty-level") {
          if (!data?.targetBuyer) return "/business-model";
          if (data.targetBuyer === "b2b") {
            if (!data?.b2b?.persona || !data?.b2b?.industry) {
              return "/customer/industry";
            }
          }
          if (data.targetBuyer === "b2c") {
            if (
              !data?.b2c?.customer ||
              !data?.b2c?.motivation ||
              !data?.b2c?.age ||
              !data?.b2c?.gender ||
              !data?.b2c?.income
            ) {
              return "/b2c/motivates";
            }
          }
        }
        if (pathName === "/recording") {
          if (!data?.targetBuyer) return "/business-model";
          if (data.targetBuyer === "b2b") {
            if (!data?.b2b?.difficulty) {
              return "/difficulty-level";
            }
          }
          if (data.targetBuyer === "b2c") {
            if (!data?.b2c?.difficulty) {
              return "/difficulty-level";
            }
          }
        }
      } else {
        return noAccessRedirect;
      }
    }
    return null;
  };

  const needsRedirect = getRedirectPath();

  // Depends on the destination itself, not just on pathName. /me can downgrade a
  // user's access after this component has mounted - an expired trial being the
  // obvious case - and with a [pathName]-only dependency that new destination was
  // never acted on: no navigation was issued, while the guard below kept rendering
  // a loader because a redirect was pending. Gating on `ready` also means the
  // decision is made from freshly loaded data rather than rehydrated state.
  useEffect(() => {
    if (ready && needsRedirect && needsRedirect !== pathName) {
      router.push(needsRedirect);
    }
  }, [ready, needsRedirect, pathName]);

  if ((needsRedirect && needsRedirect !== pathName) || !ready)
    return <Loading />;

  const handleSideBar = () => {
    dispatch(
      setIsSidebarOpen({
        isSidebarOpen: !isSidebarOpen,
      })
    );
  };
  return (
    <div className="flex w-full">
      <DashboardSidebar />
      <div className="w-full h-[calc(100vh-0px)] overflow-y-auto">
        <Menu
          className="w-6 h-6 text-black cursor-pointer block lg:hidden ms-auto my-3.5 me-3.5"
          onClick={handleSideBar}
        />
        {children}
      </div>
    </div>
  );
};

export default ClientRedirectWrapper;
